import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "./Icon";
import { PRODUCTS } from "../data/products";
import { useL } from "../lib/useLang";

const EMPTY = {
  name: "",
  email: "",
  phoneNumber: "",
  subject: "",
  message: "",
  consent: false,
  company: "", // honeypot
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactForm() {
  const { t } = useTranslation();
  const L = useL();
  const [data, setData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  // Cuánto tardó en llenarse el formulario. El servidor descarta los
  // envíos instantáneos: una persona no completa cinco campos en 3 s.
  const openedAt = useRef(Date.now());

  const change = (event) => {
    const { name, value, type, checked } = event.target;
    setData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!data.name.trim()) next.name = t("contact.required");
    if (!data.email.trim()) next.email = t("contact.required");
    else if (!EMAIL_RE.test(data.email)) next.email = t("contact.invalidEmail");
    if (!data.phoneNumber.trim()) next.phoneNumber = t("contact.required");
    if (!data.message.trim()) next.message = t("contact.required");
    if (!data.consent) next.consent = t("contact.consentRequired");
    return next;
  };

  const submit = async (event) => {
    event.preventDefault();

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      const first = document.querySelector('[aria-invalid="true"]');
      if (first instanceof HTMLElement) first.focus();
      return;
    }

    setStatus("sending");

    try {
      const response = await fetch("/api/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phoneNumber: data.phoneNumber,
          subject: data.subject || t("contact.interestOther"),
          text: data.message,
          // Señales antibot que valida el servidor: el campo trampa
          // debe llegar vacío y el tiempo de llenado debe ser humano.
          company: data.company,
          elapsed: Math.round((Date.now() - openedAt.current) / 1000),
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      setStatus("success");
      setData(EMPTY);
    } catch (error) {
      setStatus("error");
    }
  };

  const fieldProps = (name) => ({
    id: `contact-${name}`,
    name,
    value: data[name],
    onChange: change,
    disabled: status === "sending",
    "aria-invalid": errors[name] ? "true" : undefined,
    "aria-describedby": errors[name] ? `contact-${name}-error` : undefined,
  });

  const errorFor = (name) =>
    errors[name] ? (
      <p className="fsg-error-text" id={`contact-${name}-error`}>
        {errors[name]}
      </p>
    ) : null;

  return (
    <div className="fsg-form-card">
      <h3>{t("contact.formTitle")}</h3>

      <form onSubmit={submit} noValidate>
        <div className="fsg-form-grid">
          <div className="fsg-field">
            <label className="fsg-label" htmlFor="contact-name">
              {t("contact.name")}
            </label>
            <input
              className="fsg-input"
              type="text"
              autoComplete="name"
              {...fieldProps("name")}
            />
            {errorFor("name")}
          </div>

          <div className="fsg-field">
            <label className="fsg-label" htmlFor="contact-phoneNumber">
              {t("contact.phone")}
            </label>
            <input
              className="fsg-input"
              type="tel"
              autoComplete="tel"
              placeholder="787-000-0000"
              {...fieldProps("phoneNumber")}
            />
            {errorFor("phoneNumber")}
          </div>

          <div className="fsg-field fsg-field--full">
            <label className="fsg-label" htmlFor="contact-email">
              {t("contact.email")}
            </label>
            <input
              className="fsg-input"
              type="email"
              autoComplete="email"
              {...fieldProps("email")}
            />
            {errorFor("email")}
          </div>

          <div className="fsg-field fsg-field--full">
            <label className="fsg-label" htmlFor="contact-subject">
              {t("contact.interest")}
            </label>
            <select className="fsg-select" {...fieldProps("subject")}>
              <option value="">{t("contact.interestPlaceholder")}</option>
              {PRODUCTS.map((product) => (
                <option key={product.id} value={L(product.name)}>
                  {L(product.name)}
                </option>
              ))}
              <option value={t("contact.interestOther")}>
                {t("contact.interestOther")}
              </option>
            </select>
          </div>

          <div className="fsg-field fsg-field--full">
            <label className="fsg-label" htmlFor="contact-message">
              {t("contact.message")}
            </label>
            <textarea
              className="fsg-textarea"
              rows={5}
              placeholder={t("contact.messagePlaceholder")}
              {...fieldProps("message")}
            />
            {errorFor("message")}
          </div>
        </div>

        {/* Honeypot: invisible para personas, atractivo para bots. */}
        <div aria-hidden="true" className="fsg-sr-only">
          <label htmlFor="contact-company">Company</label>
          <input
            id="contact-company"
            name="company"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={data.company}
            onChange={change}
          />
        </div>

        <div className="fsg-field">
          <label className="fsg-checkbox" htmlFor="contact-consent">
            <input
              id="contact-consent"
              name="consent"
              type="checkbox"
              checked={data.consent}
              onChange={change}
              disabled={status === "sending"}
              aria-invalid={errors.consent ? "true" : undefined}
              aria-describedby={
                errors.consent ? "contact-consent-error" : undefined
              }
            />
            <span>{t("contact.consent")}</span>
          </label>
          {errors.consent && (
            <p className="fsg-error-text" id="contact-consent-error">
              {errors.consent}
            </p>
          )}
        </div>

        <button
          type="submit"
          className="fsg-btn fsg-btn--primary fsg-btn--lg fsg-btn--block"
          disabled={status === "sending"}
        >
          {status === "sending" ? t("contact.sending") : t("contact.submit")}
          {status !== "sending" && <Icon name="arrow" />}
        </button>

        <div aria-live="polite" style={{ marginTop: "var(--fsg-s-4)" }}>
          {status === "success" && (
            <div className="fsg-alert fsg-alert--success">
              <Icon name="check-circle" />
              <p>{t("contact.success")}</p>
            </div>
          )}
          {status === "error" && (
            <div className="fsg-alert fsg-alert--error">
              <Icon name="close" />
              <p>{t("contact.error")}</p>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
